// The one place for the builder's info copy (FAQ answers). Both the phone footer (MobileFooterTabs)
// and the desktop popup (ProductModal) render from this, so a wording change only happens here.
//
// Topic fields: id, title, short (phone summary), tagline + image (screens), paragraphs, items (bullet list,
// shown after the paragraphs), notes (paragraphs after the list), specialOrdersLink (adds the "Special orders"
// link line), custom (the section renders its own layout). Email addresses in the text become mailto links.
import React from 'react'

export const INFO_COPY = {
  overview: [
    {
      id: 'whyDesign',
      title: 'Why design your own microphone?',
      paragraphs: [
        "As you can see, the Ohma mic isn't just any microphone. We believe artists, engineers, and creators should feel empowered by the tools they use to make their work. We've spent years building our own gear, and that process changes how you listen. When you shape the tool yourself, you start to understand your sound in a different way.",
        "Because when you build a mic for your world, recording with it feels different. It carries your decisions, your taste, your perspective. There's nothing quite like making the tools you use, then using them to make something new."
      ]
    },
    {
      id: 'readyVsCustom',
      title: 'What is the difference between a ready-made and a custom Ohma?',
      paragraphs: [
        'Ready-made Ohmas are fully built and ready to ship in a curated set of colorways and screen combinations. A custom Ohma is built to order once you design it, with access to our full finish library and modular options. The sound and core design remain the same. The difference is time, flexibility, and the fact that your mic is built specifically for you.'
      ]
    },
    {
      id: 'buildProcess',
      title: 'What is the build process for a custom Ohma?',
      paragraphs: [
        'Once you place your order, your mic begins in our East Los Angeles workshop as raw components. We procure and mix your finish in house, and if you choose a custom HEX color, we develop a formulation specifically to match it. Your ribbon or condenser capsule is built and tuned by us, not sourced off the shelf.',
        'Every mic is built to extremely tight tolerances and tested rigorously before shipping. We run frequency sweeps, perform moisture and vocal tests for condensers, and listen critically to each unit. The full process takes four to six weeks because it does not exist until you design it. You are welcome to check in at any point along the way.'
      ]
    },
    {
      id: 'howLong',
      title: 'How long does it take to make one?',
      short: '4–6 weeks',
      paragraphs: [
        'Custom Ohmas designed here typically take four to six weeks to complete. We build and test each mic in house once your order is placed, which means it moves through the full process rather than coming from stock.',
        'If you choose a custom logo, we work with you to make sure it is exactly right before production begins, which can add time to the process. If you add a matching pop filter, it is painted alongside your mic during finishing and does not extend the build time.'
      ],
      specialOrdersLink: true
    },
    {
      id: 'whatsInBox',
      title: 'What comes in the box?',
      short: 'Mic, screens, isolation clip, picks',
      paragraphs: [
        'When your Ohma arrives, it is fully built, tuned, and ready to record. The screens you selected are installed, and the mic has already passed final testing in our East Los Angeles workshop. Inside the package you will find:'
      ],
      items: [
        'Your custom-built Ohma microphone',
        'Installed top and bottom screens',
        'Our custom isolation clip, to hold your mic on a stand and reduce vibration',
        'Two Ohma guitar picks for safely swapping screens'
      ],
      notes: [
        'If you choose custom logo screens, they are made as a matched front and back set and installed before your mic ships. Your original default screens are included in the box so you can swap them at any time.',
        'If you add a pop filter, it arrives in its own box with a desk stand.',
        'Because Ohma is modular, you can always add new screen types or finishes later.'
      ]
    },
    {
      id: 'modular',
      title: 'What makes an Ohma mic modular?',
      paragraphs: [
        'An Ohma mic is built with a fixed core and a flexible outer system. The transducer, capsule, and body finish are set as part of the build, forming the foundation of the instrument. What changes are the elements that meet the sound. Screens can be swapped to shape how the mic responds, and the pop filter integrates directly into the design rather than sitting outside of it. Screen finishes can shift as you move between sets, giving the mic a different visual and acoustic presence without replacing it. The mic itself stays the same, but how it responds is yours to shape.'
      ]
    },
    {
      id: 'gear',
      title: 'What gear do I need to use the mic?',
      short: '48V phantom power',
      paragraphs: [
        'You will need an audio interface or preamp that supplies 48V phantom power. Both our condenser and active ribbon microphones are designed to run on phantom power, so most modern recording setups are fully compatible. Unlike vintage passive ribbons, our active ribbon is built for this, so no special handling is required.'
      ]
    },
    {
      id: 'returns',
      title: 'Can I return it if it is not right for me?',
      short: '30-day guarantee',
      paragraphs: [
        'Yes. Everything is refundable within 30 days. If your Ohma does not feel right in your space, send it back. We care deeply about you feeling aligned with your instrument.',
        'Once production begins, we are not able to change the build. But Ohma was designed to evolve. Screens can be swapped, new looks can be added later, and many artists grow their setup over time.',
        'If you are unsure about mic type or screen choice, talk to us. We will help you find the right direction.'
      ]
    },
    {
      id: 'upgrade',
      title: 'Can I upgrade my mic screens later?',
      short: 'Modular by design',
      paragraphs: [
        'Absolutely. Ohma was built around modularity. Screens can be swapped in seconds, giving you the ability to fine-tune the sound or change the aesthetic of your mic. Different screens influence the tone in subtle but meaningful ways, and many artists keep multiple sets to adapt to different sessions.',
        'Your mic does not have to stay fixed. It can grow with you.'
      ]
    },
    {
      id: 'warranty',
      title: 'What is covered under warranty?',
      short: '1–2 year coverage',
      paragraphs: [
        'All Ohma microphones include a one year limited warranty for the original owner, beginning on the date of purchase. This covers internal components, electronics, and workmanship under normal use.',
        'You can extend your coverage to two years by registering your microphone online. Registration takes only a minute and ensures your warranty is fully active.'
      ]
    },
    {
      id: 'ohmieColor',
      title: 'Can I make a custom color Ohmie?',
      paragraphs: [
        'No. The Ohmie comes in anodized black. Colors, finishes, screens and custom logos are part of the Ohma Ribbon and Condenser.'
      ]
    },
    {
      id: 'talkToSomeone',
      title: 'Can I talk to someone before I order?',
      paragraphs: [
        'Yes. If you are unsure about mic type, screens, or finish, reach out to us at info@ohmaworld.com. We are happy to help you think through the options so you feel confident in your build.'
      ]
    }
  ],
  micType: [
    {
      id: 'whatIsCondenser',
      title: 'What is a condenser mic?',
      paragraphs: [
        'A condenser microphone captures sound with precision, speed, and detail. At the center of ours is The Debby, a capsule built from components we designed and tune entirely in house. Most microphone companies purchase pre-voiced capsules from external suppliers. We do not. We tune ours ourselves, a deliberate process that can take weeks as we refine tension, balance, and response until it behaves exactly the way we want it to.',
        'The Debby is a cardioid, edge-terminated, single-backplate design. That architecture allows the capsule to respond fully to lower frequencies while maintaining clarity and control. Our condenser is not a clone of a vintage mic or a copy of an existing circuit. Its voicing was developed over years of iteration. Each condenser is designed, built, tuned, and tested in our East Los Angeles workshop and has a built-in pad for handling louder sources when needed.'
      ]
    },
    {
      id: 'condenserSound',
      title: 'How does the Ohma condenser sound?',
      paragraphs: [
        'The Ohma condenser is forward without being hyped. The top end is articulate and open, but it remains warm and controlled rather than artificially bright. Many condensers build brightness directly into the voicing. When that brightness turns harsh, it can be difficult to tame. We chose a different philosophy.',
        'Because of its edge-terminated design, the low end responds naturally and fully, giving you bass that feels dimensional and defined rather than bloated. The midrange remains intact instead of scooped, which keeps vocals and instruments feeling grounded and real. You can add air with EQ when you want it, but you are not fighting brittleness from the start. Transients are fast and controlled. Switch on the built-in pad and it remains composed even on louder sources like amps or kick drums.'
      ]
    },
    {
      id: 'condenserScreens',
      title: 'How do screens affect the condenser?',
      paragraphs: [
        'Mic type defines the voice. Screens shape how that voice is presented. On the Ohma condenser, the screen is not cosmetic. It directly influences how the capsule interacts with the source. Because The Debby is highly responsive, different screen geometries can meaningfully shift tonal balance, midrange focus, and low-frequency behavior.',
        'More open screens allow the mic to retain more low-end energy at a distance, often resulting in a darker capture with stronger bass presence. Certain patterned designs can accentuate midrange detail, bringing clarity and focus to vocals or instruments. Others improve plosive control or rebalance the frequency response by tempering low-end buildup while subtly lifting perceived highs. These are not EQ tricks after the fact. They are acoustic changes happening before the sound reaches the capsule. Head to the Screen Type tab to explore how each design interacts with the condenser differently.'
      ]
    },
    {
      id: 'whatIsRibbon',
      title: 'What is a ribbon mic?',
      paragraphs: [
        'A ribbon microphone captures sound using a thin aluminum ribbon suspended within a magnetic field. When sound waves move the ribbon, it passes through that magnetic field and generates voltage. That magnetic transducer assembly is called the motor because it converts air movement directly into electricity.',
        'Our ribbon is figure-8, capturing sound equally from the front and back while rejecting the sides. We tension and tune every ribbon in house in our East Los Angeles workshop. We designed and machined our own ribbon crimping tool to control how the aluminum is set and secured. The aluminum itself is produced in Italy by a fourth-generation manufacturer with nearly a century of experience working in metal leaf. Our ribbon is active and requires phantom power. The onboard electronics provide stable output and low noise across standard interfaces and long cable runs.'
      ]
    },
    {
      id: 'ribbonSound',
      title: 'How does the ribbon sound?',
      paragraphs: [
        'Ribbon microphones are known for sounding classic because of how they treat high frequencies. The top end is smooth and pleasant rather than hyped. Instead of exaggerating brightness, it keeps the sound grounded and controlled.',
        'The low end is thick and full, especially up close due to the strong proximity effect inherent in figure-8 designs. That bloom feels weighty and dimensional. The midrange carries density and realism, which is why ribbons often feel immersive. Because the high end is not hyped, the mic takes high-shelf EQ naturally if you want more air and presence. You are shaping from a balanced foundation rather than correcting harshness.'
      ]
    },
    {
      id: 'ribbonScreens',
      title: 'How do screens affect the ribbon?',
      paragraphs: [
        'On a figure-8 ribbon, screen geometry plays a significant role. Because the ribbon responds to airflow from both the front and back, different screen designs influence how low frequencies build, how midrange energy projects, and how proximity effect develops.',
        'More open screens allow low-end bloom to expand more freely and can result in a darker, fuller capture at distance. More structured designs can temper low-end buildup, refine plosive behavior, and rebalance lows and highs simultaneously. With the ribbon in particular, screen choice meaningfully shapes the voice. The motor defines the foundation. The screen defines how that foundation interacts with space.'
      ]
    },
    {
      id: 'whatIsPad',
      title: 'What is a pad?',
      paragraphs: [
        "A pad reduces the microphone's output level so it can handle louder sound sources without distorting.",
        'On the Ohma condenser, engaging the pad lowers the signal by -16.5 dB, giving you more headroom when recording things like drums, loud vocals, or amplified instruments. Use it when the mic or your preamp is being pushed too hard, or when you want a cleaner, more controlled signal on high-volume sources.'
      ]
    }
  ],
  screenType: [
    {
      id: 'whatIsScreen',
      title: 'What is an Ohma screen?',
      paragraphs: [
        'A screen is the patterned metal grille on the front and back of your mic. It protects the ribbon or capsule, defines the visual character of the instrument, and functions as part of the acoustic system.',
        'Microphones translate air into electricity. Before that air reaches the ribbon or diaphragm, it passes through the screen. The geometry of that structure influences airflow, pressure distribution, and how different wavelengths scatter at the surface of the transducer. Change the structure, and the response changes in measurable ways.',
        'Screens attach magnetically and can be removed with a guitar pick or the edge of a slim card. For consistent sonic performance, use the same screen on the front and back. Bottom screens are ornamental only.'
      ]
    },
    {
      id: 'howScreensAffect',
      title: 'How do screens affect my recordings?',
      paragraphs: [
        'It changes the shape of what the microphone captures before you reach for EQ. A more open screen allows air to move more freely across the ribbon or capsule. That can result in greater low-end development, more room information, and a sense of openness around the source.',
        'A more controlled geometry introduces slightly more resistance. That can tighten proximity effect, focus the midrange, manage plosives, and center the image when working close to a vocal or amp.',
        'These differences are not extreme redesigns of the mic. They are structural refinements. Subtle shifts in airflow create measurable shifts in frequency balance and transient behavior, which means you can adjust how the mic responds to the source rather than correcting it later.',
        'In practice, that means you can track a vocal that feels more open and dimensional, tighten a guitar without over-EQing, or prevent stacked midrange buildup when recording multiple layers with the same mic. The goal is not to replace mixing. It is to shape the capture so the sound starts closer to where you want it.'
      ]
    },
    {
      id: 'whyNotMesh',
      title: "Why doesn't Ohma use a standard mesh grille?",
      paragraphs: [
        'Because the grille is not just protection. It is the first structure air encounters before reaching the ribbon or capsule, and that interaction changes the response.',
        'We spent years working with vintage ribbon microphones where structure, ornament, and acoustics were inseparable. The grille was not a cover. It shaped how air first met the ribbon, and that interaction changed the response.',
        'Once we saw that clearly, modern mesh felt incomplete. It protected the transducer, but it did not meaningfully participate in the sound. So we started cutting patterns and testing them. Each configuration altered airflow and shifted the frequency balance in measurable ways. Some designs were visually strong but acoustically unusable. Others sounded promising but overlapped too closely in character. We refined the geometries until the remaining patterns produced distinct responses.',
        'That work became the magnetic screen system. The structures are fixed and intentional. The ability to choose between them is yours.'
      ]
    },
    {
      id: 'whenToUse',
      title: 'When should I use each screen type?',
      paragraphs: [
        "Swapping screens lets you shape the mic's response at the source. When you record multiple sources with the same microphone, certain frequency curves can begin to stack in a mix. A structural change at the grille shifts how the mic receives air, altering the balance before you ever reach for EQ.",
        'It also changes how the mic behaves at distance or up close. More open geometries allow greater low-end development and room information. More controlled patterns can tighten the center image and better manage plosives when working close to the source.',
        'That flexibility encourages experimentation. You can track a vocal one way in the morning and reframe it in the afternoon without changing microphones. Over time, you begin to understand how structure shapes tone, and the mic becomes something you actively tune rather than something fixed.',
        'Many companies release separate models to achieve these variations. We chose a different path. Instead of locking a microphone into one permanent architecture, we designed it to evolve.'
      ]
    },
    {
      id: 'doesFabricType',
      title: 'Does fabric type or the bottom screen affect the sound?',
      paragraphs: [
        'No. The bottom screen and fabric are purely visual choices and do not affect the sound of the microphone.',
        'If you choose no fabric, the lower section will be open, revealing the body color beneath the logo.'
      ]
    },
    {
      id: 'motif',
      title: 'Motif',
      tagline: 'The mix-ready Ohma sound.',
      image: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Motif.webp?v=1773902656',
      paragraphs: [
        'If you want the version of an Ohma mic that feels complete the moment you press record, this is it. Motif is the screen we tuned our Debby condenser capsule around. It represents the core balance of the system.',
        'Motif excels on lead vocals, overheads, guitars, keys, and almost any source where you want clarity without extra work later. It centers the image and keeps things controlled while still feeling open.',
        'On the condenser, Motif delivers a broad lift in the presence range from roughly 8 to 13.5 kHz, followed by a strong taper above about 16.5 to 17 kHz. That combination gives you air without brittle edges. The low end begins gently easing back around 200 Hz, which keeps body intact while preventing buildup.',
        'On the ribbon, a wide lift around 11 to 13 kHz adds openness while preserving smoothness. The controlled low end helps manage proximity effect so close sources stay tight.',
        'If you are unsure where to begin, begin here.'
      ]
    },
    {
      id: 'scales',
      title: 'Scales',
      tagline: 'Clarity with definition.',
      image: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Scales.webp?v=1773891830',
      paragraphs: [
        'Scales is for moments when top-end articulation matters more than weight. It keeps the Ohma body intact but refines it, giving vocals and instruments a more defined edge.',
        'This screen excels on pop vocals, spoken word, stacked harmonies, acoustic guitars, and dense productions where clarity is everything. It helps a source step forward without sounding hyped.',
        'Scales slightly reduces low-frequency density compared to Motif, which shifts perception upward and makes the mic feel brighter and more precise. It also retains a bit more energy in the highest frequencies than Motif, adding a sense of openness without harshness.',
        'On condenser, the result is articulate and polished. On ribbon, it preserves smoothness while trimming some of the extra weight that ribbons can naturally carry.',
        'If you want precision without losing character, choose Scales.'
      ]
    },
    {
      id: 'windows',
      title: 'Windows',
      tagline: 'Space and dimension.',
      image: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Windows.webp?v=1773902656',
      paragraphs: [
        'Windows is for capturing the environment as much as the source. It is the most open geometry in the system, designed to interfere as little as possible with the ribbon or condenser capsule.',
        'This screen excels on orchestras, drum overheads, pianos, room mics, and distant placements. It allows space to become part of the recording.',
        'Ohma mics naturally carry body, and Windows lets that full character come through. It has the strongest proximity effect in the lineup and begins tapering the top end earlier than the others, giving it a darker presence range while still retaining air in the highest frequencies.',
        'On condenser, Windows offers a natural, spacious presentation that avoids modern harshness. On ribbon, it behaves like a classic far-field capture, open and dimensional.',
        'If you want the room to breathe in the recording, choose Windows.'
      ]
    },
    {
      id: 'stripes',
      title: 'Stripes',
      tagline: 'Focus at the source.',
      image: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Stripes.webp?v=1773895000',
      paragraphs: [
        'Stripes is about control before the signal ever reaches your preamp. By slightly reducing low-end energy and tapering the upper top end, Stripes emphasizes the midrange. Its geometry physically bisects the center of the ribbon and condenser capsule, altering how energy reaches the diaphragm.',
        'It excels on guitar amps, acoustic instruments, punchy vocals, and situations where you want a source to feel forward and contained. It naturally reduces plosives and keeps room tone from overwhelming the capture.',
        'The Stripes screen is entirely intentional: geometry shaping tone at the source. It alters how wavefronts reach the diaphragm, introducing a form of acoustic lensing. The result is a more focused, mix-ready image without added processing.',
        'On the condenser, Stripes tightens the frame and helps vocals cut through. On ribbon, it tempers proximity effect while preserving character.',
        'Visually inspired by vintage B&O design, Stripes shapes tone through structure.'
      ]
    },
    {
      id: 'holes',
      title: 'Holes',
      tagline: 'The most linear expression of the Ohma capsule.',
      image: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Holes.webp?v=1773902656',
      paragraphs: [
        'If you want to hear the capsule without added presence lift or tilt shaping, this is the screen. Ohma microphones naturally carry body. That fullness is part of our tuning. Holes preserves that character but distributes it more evenly across the spectrum.',
        'The low end feels substantial without rising into a pronounced bloom. The midrange remains smooth and unpushed. The top end tapers naturally without added lift. Unlike Motif, Holes does not introduce a broad presence boost. Unlike Windows, it does not tilt darker through early high-frequency taper. The result is the most even contour in the lineup.',
        'This screen excels on bass instruments, kick drums, piano, strings, woodwinds, and full-range sources where you want balance instead of emphasis. It keeps depth without spotlighting any one region.',
        'On condenser, Holes delivers weight and clarity without added shaping in the upper mids. On ribbon, it maintains mass while keeping proximity effect steady and predictable.',
        'If you want the most linear path through the Ohma sound, choose Holes.'
      ]
    }
  ],
  customLogo: [
    {
      id: 'whatIsLogo',
      title: 'What is a custom logo?',
      paragraphs: [
        'A custom logo turns your microphone into a signature piece with two magnetic bottom screens cut with your design. It is a way to bring your branding, artist mark, or personal symbol directly into the mic.',
        'It includes two custom logo magnetic bottom screens, color matching to your chosen finish, artwork review by our team, a digital render for approval when needed, and guidance if your design needs to be adapted for production.',
        'We recommend emailing your artwork to sammy@ohmaworld.com before ordering so we can confirm whether it is a good fit for laser cutting. If you already know which microphone you want, send us a link to that build too.'
      ]
    },
    {
      id: 'logoProcess',
      title: 'What is the custom logo process?',
      paragraphs: [
        'Email your logo to sammy@ohmaworld.com before ordering, if possible. If you already know which microphone you want, send us a link to that build too.',
        'We review your design to see whether it will work well as a laser cut screen. If needed, we suggest adjustments or discuss another way to apply your design on a case by case basis.',
        'Once your artwork is approved, we can move your order forward with more confidence. If you send your file after ordering instead, we will still review it before production begins.',
        'If needed, we send a digital mockup for approval before making your custom parts.'
      ]
    },
    {
      id: 'whatLogos',
      title: 'What logos can you make?',
      paragraphs: [
        'The best results usually come from simple, bold artwork that can be clearly translated into metal. Great options include artist logos, studio marks, podcast or channel branding, personal monograms, and symbols or icons.',
        'We recommend sending your artwork to sammy@ohmaworld.com before ordering so we can confirm whether it is suitable for laser cutting. If it is not, we can let you know whether another approach may be possible.'
      ]
    },
    {
      id: 'sendLogo',
      title: 'How do I send my logo?',
      paragraphs: [
        'Email your artwork or a file link to sammy@ohmaworld.com. We recommend sending it before ordering so we can review it first.',
        'If you already know which microphone you want, include a link to that build in your email. That helps us review everything faster and get your project moving sooner. You can also send your logo after placing your order, as long as production has not started yet.'
      ]
    },
    {
      id: 'fileTypes',
      title: 'What file types work best?',
      paragraphs: [
        'Vector files work best because they give us the cleanest result for laser cutting. Preferred file types are AI, PDF, and SVG.',
        'If you do not have a vector file, you can still send a clean high resolution PNG with a transparent background. Email your file to sammy@ohmaworld.com and we will review it before production. If needed, we will let you know whether it should be revised or handled another way.'
      ]
    },
    {
      id: 'designsWork',
      title: 'What designs work best?',
      paragraphs: [
        'Clear shapes, distinct lines, and bold silhouettes usually translate best. Very tiny details, delicate connecting points, gradients, or textured effects may not work well for laser cutting.',
        'That is why we recommend sending your design in before ordering. We can review it ahead of time and let you know whether it is ready for laser cutting or whether another option may make more sense.'
      ]
    },
    {
      id: 'mockup',
      title: 'Can I see a mockup before you make it?',
      paragraphs: [
        'Yes. If your design needs review or adaptation, we send a digital proof before production. Nothing moves forward until the design is approved.',
        'For the smoothest process, send your artwork to sammy@ohmaworld.com before ordering so we can confirm that your logo is a good fit first.'
      ]
    },
    {
      id: 'limits',
      title: 'Are there limits to what can be laser cut?',
      paragraphs: [
        'Yes. Designs that are too thin, fragile, overly complex, or not permitted for use may need to be modified or handled another way. Some logos are a great fit for laser cutting, while others may be better suited to a different method.',
        'Because of that, we strongly recommend emailing your artwork before ordering. We will review it and let you know what is possible on a case by case basis.'
      ]
    },
    {
      id: 'sendBefore',
      title: 'Should I send my logo before ordering?',
      paragraphs: [
        'Yes, we recommend it. Sending your logo to sammy@ohmaworld.com before ordering gives us a chance to confirm whether it will work for laser cutting, or whether another method may be needed.',
        'If you already have a microphone build in mind, send us that link too. Reviewing the logo and microphone together helps us move faster, avoid delays, and get your order started sooner.'
      ]
    },
    {
      id: 'popFilter',
      title: 'Will my logo go on my pop filter?',
      paragraphs: [
        'Yes. If you order a custom logo mic and a custom pop filter, your pop filter will come with a matching custom logo screen attached. You will also receive the two custom logo bottom screens included with the mic.'
      ]
    }
  ],
  popFilter: [
    {
      id: 'whatItDoes',
      title: 'What does the Ohma Pop Filter actually do?',
      paragraphs: [
        'The Ohma Pop Filter reduces plosives, moisture, and harsh bursts of air before they reach the mic, helping vocals come through cleaner and more consistent. It is designed to make close recording easier without turning the setup into something fussy or overbuilt.',
        'It snaps directly into the bottom screen slot, replacing the logo screen and locking the filter into a fixed, deliberate position. That placement is not adjustable by design. It keeps your voice at a more consistent distance where the mic performs best, making takes feel more stable and repeatable.',
        "Its internal structure uses dual mesh, a tuned air gap, and low-resistance acoustic fabric to control fast-moving air before it hits the capsule or ribbon. The goal is to manage plosives without getting in the way of the mic's natural character.",
        'It is especially useful on vocals, podcasting, streaming, kick drum, and loud speaker sources that move a lot of air. On the condenser, it helps control plosives and moisture while keeping close recordings more controlled. On the ribbon, it adds an extra layer of protection from direct air bursts.',
        'This design came out of extended in-house development, with placement, spacing, and materials refined together as part of the Ohma system rather than treated as an afterthought.'
      ]
    },
    {
      id: 'howDifferent',
      title: 'How is it different from a generic pop filter?',
      paragraphs: [
        'Most pop filters are universal accessories. They attach with clamps, flexible arms, and adjustable positioning, which means the distance changes, the setup shifts, and the result can vary from take to take.',
        'The Ohma Pop Filter is built as part of the microphone system. It snaps into the bottom screen slot, replaces the logo screen, and locks into a fixed position that keeps spacing consistent every time you record.',
        'That consistency is the point. Instead of constantly adjusting placement, the filter holds the distance where the mic performs best, helping you get more repeatable takes without extra setup.',
        "It is also designed to control air without getting in the way of the mic's character. Rather than acting like a barrier in front of the mic, it manages plosives before they become a problem while keeping the sound open and usable."
      ]
    },
    {
      id: 'whichOption',
      title: 'Which pop filter option should I choose?',
      custom: true
    },
    {
      id: 'alreadyHaveOne',
      title: 'Do I still need an Ohma Pop Filter if I already have a generic one?',
      paragraphs: [
        'You can use most pop filters with an Ohma mic, but this one is designed specifically for the Ohma system. It keeps the setup cleaner, locks in the correct spacing, and removes the need for clamps, arms, or repositioning.'
      ]
    },
    {
      id: 'howFast',
      title: 'How quickly can I remove it or switch it out?',
      paragraphs: [
        'It can be removed in seconds. It uses the same magnetic interface as the rest of the Ohma system, so swapping between configurations is fast and simple.'
      ]
    },
    {
      id: 'customLogo',
      title: 'If I order a custom logo, will it be on the pop filter?',
      paragraphs: [
        'Yes. If you order a custom logo mic and a custom pop filter, your pop filter will come with a matching custom logo screen attached. You will also receive the two custom logo bottom screens included with the microphone.'
      ]
    }
  ]
}

// Spec sheet rows, word for word from the Manual page (ohmaworld.com/pages/manual). Update both together.
export const MIC_SPECS = {
  condenser: [
    ['Operating principle', 'Condenser, pressure gradient transducer'],
    ['Polar pattern', 'Cardioid'],
    ['Frequency range', '20 Hz to 20 kHz'],
    ['Sensitivity at 1 kHz', '15.5 mV/Pa (−36.2 dBV/Pa)'],
    ['Max SPL (at 1 kHz)', '118 dB, about 134 dB with the pad'],
    ['Self-noise (A-wtd)', '16 dB-A'],
    ['Signal-to-noise (A-wtd, re 94 dB SPL)', '78 dB'],
    ['Pad', '−16.5 dB switch, under the bottom screen on the back (mics made since May 2026)'],
    ['Circuit', 'Transformer-coupled dual FET'],
    ['Nominal impedance', '150 Ω'],
    ['Rated load impedance', '1 kΩ or greater'],
    ['Power', '48V phantom (P48), 0.5 mA'],
    ['Connector', '3-pin XLR, pin 2 hot'],
    ['Size', '7.5 x 1.9 x 0.9 in (191 x 48 x 23 mm)'],
    ['Weight', '211 g without screens. A screen set adds 96 g.']
  ],
  ribbon: [
    ['Operating principle', 'Ribbon, pressure gradient transducer'],
    ['Polar pattern', 'Figure-8 (bidirectional)'],
    ['Frequency range', '20 Hz to 20 kHz'],
    ['Sensitivity at 1 kHz', '7.9 mV/Pa (−42.1 dBV/Pa)'],
    ['Max SPL (at 1 kHz)', '140 dB'],
    ['Self-noise (A-wtd)', '19 dB-A'],
    ['Signal-to-noise (A-wtd, re 94 dB SPL)', '75 dB'],
    ['Circuit', 'Dual active FET'],
    ['Nominal impedance', '92 Ω'],
    ['Rated load impedance', '1 kΩ or greater'],
    ['Power', '48V phantom (P48), 7 mA. Active ribbon, so phantom power is safe.'],
    ['Connector', '3-pin XLR, pin 2 hot'],
    ['Size', '7.5 x 1.9 x 0.9 in (191 x 48 x 23 mm)'],
    ['Weight', '168 g without screens. A screen set adds 96 g.']
  ]
}

// id -> topic, per section (desktop looks topics up by id)
export const INFO_COPY_BY_ID = Object.fromEntries(
  Object.entries(INFO_COPY).map(([section, topics]) => [section, Object.fromEntries(topics.map((t) => [t.id, t]))])
)

const EMAIL_RE = /([a-z0-9._-]+@ohmaworld\.com)/i

// Turns any ohmaworld.com email in a line of copy into a mailto link (extraProps: e.g. hover handlers)
export function linkifyCopy(text, extraProps) {
  if (typeof text !== 'string' || !EMAIL_RE.test(text)) return text
  return text
    .split(EMAIL_RE)
    .map((part, i) =>
      i % 2 === 1
        ? React.createElement(
            'a',
            { key: i, href: `mailto:${part}`, style: { color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }, ...(extraProps || {}) },
            part
          )
        : part
    )
}

// Option rows for the desktop two-column lists
export const topicOptions = (topics) => topics.map((t) => ({ id: t.id, label: t.title, short: '', ...(t.image ? { image: t.image } : {}) }))
